import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Paperclip, Upload, Trash2, FileText, ImageIcon, Loader2, FolderTree } from "lucide-react";
import { toast } from "sonner";

const MAX_MB = 25;
const isImage = (ct) => ct?.startsWith("image/");

export default function Attachments({ kind, parentId, canDelete = true }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(""); // "" = all, "__unfiled" = null folder
  const [uploadFolder, setUploadFolder] = useState("");
  const fileRef = useRef(null);

  const supportsFolders = kind === "project";

  const load = useCallback(() => {
    api.get(`/attachments/${kind}/${parentId}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [kind, parentId]);

  const loadFolders = useCallback(() => {
    if (!supportsFolders) return;
    api.get(`/attachments/folders/${kind}`).then((r) => setFolders(r.data.folders || [])).catch(() => setFolders([]));
  }, [kind, supportsFolders]);

  useEffect(() => { if (parentId) { load(); loadFolders(); } }, [load, loadFolders, parentId]);

  const onFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > MAX_MB * 1024 * 1024) { toast.error(`${f.name} exceeds ${MAX_MB}MB`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        const url = `/attachments/${kind}/${parentId}${uploadFolder ? `?folder=${encodeURIComponent(uploadFolder)}` : ""}`;
        await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Uploaded");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const openFile = async (rec) => {
    try {
      const resp = await api.get(`/attachments/download/${rec.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
    } catch { toast.error("Download failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this attachment?")) return;
    await api.delete(`/attachments/${id}`);
    toast.success("Removed"); load();
  };

  const moveToFolder = async (id, folder) => {
    try {
      await api.patch(`/attachments/${id}`, { folder });
      load(); toast.success("Moved");
    } catch (e) { toast.error(e.response?.data?.detail || "Move failed"); }
  };

  const filtered = activeFolder === ""
    ? items
    : activeFolder === "__unfiled"
      ? items.filter((f) => !f.folder)
      : items.filter((f) => f.folder === activeFolder);

  const countIn = (name) => name === "__unfiled"
    ? items.filter((f) => !f.folder).length
    : items.filter((f) => f.folder === name).length;

  return (
    <div data-testid={`attachments-${kind}-${parentId}`}>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Paperclip className="h-4 w-4 text-primary" />
          <h3 className="font-display font-semibold">Attachments</h3>
          <span className="text-xs text-muted-foreground">{items.length}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {supportsFolders && (
            <Select value={uploadFolder || "none"} onValueChange={(v) => setUploadFolder(v === "none" ? "" : v)}>
              <SelectTrigger className="w-44 h-9" data-testid="upload-folder-select"><SelectValue placeholder="Choose folder…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unfiled</SelectItem>
                {folders.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <input
            ref={fileRef} type="file" multiple className="hidden"
            data-testid={`attach-input-${kind}`}
            onChange={(e) => onFiles(Array.from(e.target.files || []))}
          />
          <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`attach-upload-${kind}`}>
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            Upload
          </Button>
        </div>
      </div>

      {supportsFolders && folders.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3 pb-2 border-b border-border" data-testid="folder-tabs">
          <FolderChip active={activeFolder === ""} label="All" count={items.length} onClick={() => setActiveFolder("")} icon={<FolderTree className="h-3 w-3" />} />
          {folders.map((f) => (
            <FolderChip key={f} active={activeFolder === f} label={f} count={countIn(f)} onClick={() => setActiveFolder(f)} />
          ))}
          <FolderChip active={activeFolder === "__unfiled"} label="Unfiled" count={countIn("__unfiled")} onClick={() => setActiveFolder("__unfiled")} />
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded">
          No files in this view. Max {MAX_MB}MB per file.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {filtered.map((f) => (
            <div key={f.id} className="flex items-center gap-3 p-2.5 rounded border border-border bg-secondary/30 card-hover">
              <div className="h-9 w-9 rounded bg-primary/10 border border-primary/30 grid place-items-center shrink-0">
                {isImage(f.content_type) ? <ImageIcon className="h-4 w-4 text-primary" /> : <FileText className="h-4 w-4 text-primary" />}
              </div>
              <button className="flex-1 min-w-0 text-left" onClick={() => openFile(f)}>
                <div className="text-sm font-medium truncate">{f.original_filename}</div>
                <div className="text-xs text-muted-foreground font-mono">
                  {(f.size / 1024).toFixed(0)} KB · by {f.uploaded_by_name}
                  {f.folder && <span className="ml-1 text-primary/80">· {f.folder}</span>}
                </div>
              </button>
              {supportsFolders && (
                <Select value={f.folder || "__unfiled"} onValueChange={(v) => moveToFolder(f.id, v === "__unfiled" ? "" : v)}>
                  <SelectTrigger className="w-32 h-8 text-xs" data-testid={`move-folder-${f.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__unfiled">Unfiled</SelectItem>
                    {folders.map((fold) => <SelectItem key={fold} value={fold}>{fold}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {canDelete && (
                <Button variant="ghost" size="icon" onClick={() => remove(f.id)} className="h-8 w-8" data-testid={`attach-delete-${f.id}`}>
                  <Trash2 className="h-4 w-4 text-red-400" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FolderChip({ active, label, count, onClick, icon }) {
  return (
    <button onClick={onClick}
            className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border transition-colors ${
              active ? "bg-primary/20 border-primary/50 text-primary" : "bg-secondary/30 border-border hover:border-primary/40 text-muted-foreground"
            }`}>
      {icon}
      {label}
      <span className="font-mono opacity-70">{count}</span>
    </button>
  );
}
